/**
 * ChargeNest — Konto-Verwaltung (Appwrite Account API)
 * =====================================================
 * Ausgelagert aus dem AppContext, damit die Aufrufe testbar sind.
 *
 * Appwrite 1.8 / SDK 16 (Stand 27.10.2026, am Live-Server verifiziert):
 *   - Passwort ändern  → PATCH /account/password  { password, oldPassword }
 *                        SDK: account.updatePassword(neuesPasswort, altesPasswort)
 *   - Name ändern       → PUT  /account/name       { name }
 *                        SDK: account.updateName(neuerName)
 *
 * ACHTUNG: Das ALTE Passwort muss das ZWEITE Argument von updatePassword sein.
 * Bei vertauschter Reihenfolge antwortet Appwrite mit 401 "Invalid credentials",
 * obwohl das neue Passwort korrekt wäre.
 */

export const PASSWORD_MIN_LENGTH = 8;

/**
 * Fehler der Konto-API in eine verständliche Meldung übersetzen.
 * @param {object} error  AppwriteException
 * @param {string} fallback  Standardtext, wenn der Fehler unbekannt ist
 */
export const describeAccountError = (error, fallback) => {
  const code = error?.code;
  const type = error?.type || '';

  if (code === 401 && type === 'user_invalid_credentials') {
    return 'Das aktuelle Passwort ist falsch.';
  }
  if (code === 401) {
    return 'Keine aktive Anmeldung – bitte neu anmelden und erneut versuchen.';
  }
  if (code === 400 && (type === 'general_argument_invalid' || type === 'user_password_policy_violated')) {
    return `Das neue Passwort wird nicht akzeptiert – mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`;
  }

  // Reihenfolge zaehlt: Appwrite nutzt 409 fuer mehrere Konflikte.
  // Deshalb zuerst den konkreten Typ pruefen, erst danach auf den Code 409
  // zurueckfallen - sonst schluckt der Passwort-Block jede andere 409-Meldung.
  if (type === 'user_password_mismatch') {
    return 'Das neue Passwort darf nicht identisch mit dem alten sein.';
  }
  if (type === 'user_already_exists') {
    return 'Dieser Name ist bereits vergeben.';
  }
  if (code === 409) {
    return 'Die Angabe kollidiert mit einem bestehenden Eintrag.';
  }
  return error?.message ? `${fallback} (${error.message})` : fallback;
};

/**
 * Passwort des angemeldeten Kontos ändern.
 * @param {object} account  Appwrite Account-Instanz
 * @param {string} currentPassword  aktuelles Passwort
 * @param {string} newPassword  neues Passwort
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const changeAccountPassword = async (account, currentPassword, newPassword) => {
  if (!currentPassword) {
    return { success: false, error: 'Bitte das aktuelle Passwort eingeben.' };
  }
  if (!newPassword || newPassword.length < PASSWORD_MIN_LENGTH) {
    return { success: false, error: `Das neue Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.` };
  }
  if (currentPassword === newPassword) {
    return { success: false, error: 'Das neue Passwort muss sich vom aktuellen unterscheiden.' };
  }
  try {
    // Reihenfolge laut Appwrite 1.8: (neues Passwort, altes Passwort)
    await account.updatePassword(newPassword, currentPassword);
    return { success: true };
  } catch (error) {
    return { success: false, error: describeAccountError(error, 'Passwort konnte nicht geändert werden.') };
  }
};

/**
 * Anzeigename des angemeldeten Kontos ändern.
 * @param {object} account  Appwrite Account-Instanz
 * @param {string} newName  neuer Anzeigename
 */
export const changeAccountName = async (account, newName) => {
  const trimmed = String(newName || '').trim();
  if (!trimmed) return { success: false, error: 'Der Name darf nicht leer sein.' };
  try {
    const updated = await account.updateName(trimmed);
    return { success: true, name: updated?.name || trimmed };
  } catch (error) {
    return { success: false, error: describeAccountError(error, 'Benutzername konnte nicht geändert werden.') };
  }
};

/**
 * Fehler beim Anfordern eines Passwort-Reset-Links verstaendlich machen.
 *
 * Wichtig: Appwrite antwortet bei unbekannter Adresse mit 404
 * ("User with the requested ID could not be found"). Ohne eigene Meldung
 * bleibt der User bei einem generischen Fehler stehen und weiss nicht,
 * dass die E-Mail-Adresse nicht zum Konto passt.
 *
 * @param {object} error  AppwriteException
 * @returns {string} Meldung fuer die Oberflaeche
 */
export const describeRecoveryError = (error) => {
  const code = error?.code;
  const type = error?.type || '';

  if (code === 404 || type === 'user_not_found') {
    return 'Zu dieser E-Mail-Adresse gibt es kein Konto. Bitte die Adresse prüfen – sie muss exakt der beim Konto hinterlegten Adresse entsprechen.';
  }
  if (code === 429 || type === 'general_rate_limit_exceeded') {
    return 'Zu viele Versuche. Bitte kurz warten und es erneut versuchen.';
  }
  if (code === 401) {
    return 'Der Link konnte nicht angefordert werden. Bitte später erneut versuchen.';
  }
  return 'Der Link konnte nicht angefordert werden. Bitte später erneut versuchen.';
};

export default {
  PASSWORD_MIN_LENGTH,
  describeAccountError,
  describeRecoveryError,
  changeAccountPassword,
  changeAccountName,
};
